import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import toast from 'react-hot-toast';
import AuthGuard from '../components/AuthGuard';

interface Tier {
  id: number;
  shares_amount: number;
  daily_withdrawal_limit: number;
  price: number;
}

interface WalletInfo {
  equity_balance: number;
  daily_withdrawal_limit: number;
  quata_total: number;
}

interface PurchaseRecord {
  id: number;
  tier_name: string;
  shares_amount: number;
  daily_withdrawal_limit: number;
  price: number;
  status: string;
  created_at: string;
}

interface ShareInfoData {
  market_cap: string;
  equity_type: string;
  exchange: string;
  total_shares: string;
  per_person_limit: string;
  unit_price: string;
  unit_price_note: string;
  user_total_shares: number;
  user_equity_value: number;
}

export default function Share() {
  const [info, setInfo] = useState<ShareInfoData | null>(null);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [wallet, setWallet] = useState<WalletInfo | null>(null);
  const [purchasing, setPurchasing] = useState<number | null>(null);
  const [confirmTier, setConfirmTier] = useState<Tier | null>(null);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [history, setHistory] = useState<PurchaseRecord[]>([]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('status') === 'pending') {
      setShowPendingModal(true);
      setSearchParams({}, { replace: true });
    }
  }, []);

  useEffect(() => {
    api.get('/share/info').then(({ data }) => setInfo(data?.data ?? data)).catch(() => { });
    api.get('/share/tiers').then(({ data }) => setTiers(Array.isArray(data) ? data : data.data ?? data.items ?? [])).catch(() => { });
    api.get('/share/wallet').then(({ data }) => setWallet(data?.data ?? data)).catch(() => { });
  }, []);

  async function purchase(tierId: number) {
    setPurchasing(tierId);
    setConfirmTier(null);
    try {
      const { data } = await api.post('/share/purchase', { tier_id: tierId });
      const payUrl = data?.data?.pay_url;
      if (payUrl) {
        window.location.href = payUrl;
      } else {
        toast.success('订单已创建');
        api.get('/share/wallet').then(({ data }) => setWallet(data?.data ?? data)).catch(() => { });
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? '购买失败');
    } finally {
      setPurchasing(null);
    }
  }

  function openHistory() {
    setShowHistoryModal(true);
    api.get('/share/history').then(({ data }) => setHistory(data?.data ?? [])).catch(() => { });
  }

  return (
    <div className="max-w-md mx-auto min-h-screen pb-[50px] "
      style={{
        backgroundImage: 'url(/images/share_beijing.png)',
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'top center',
      }}>
      <AuthGuard />

      {/* Header background */}
      <div
        className="relative pt-4 px-4"
      >
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-black/20 text-white"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
        </div>
        <div className="flex flex-col items-center gap-3 mt-2">
          <img src="/images/share_logo.png" alt="" width={250} />
        </div>
      </div>

      {/* Equity details card */}
      <div className="px-4 mt-[30px] relative z-10">
        <div
          className="rounded-2xl px-3 pt-4 pb-3 shadow-sm relative"
          style={{
            backgroundImage: 'url(/images/share_fendi.png)',
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="flex justify-center mb-2">
            <div className="relative mt-[-25px]">
              <img src="/images/share_biaoti.png" alt="云数家园 财富股权" width={200} />
              <h3 className="text-base text-white absolute left-1/2 top-[5px] -translate-x-[50%]">股权详情</h3>
            </div>
          </div>

          {info ? (
            <div className="space-y-2.5 bg-white rounded-md p-3">
              <div className="flex justify-between">
                <span className="flex-1 text-sm text-gray-600">股权市值：<span className="text-[#c4161c]">{info.market_cap}</span></span>
                <span className="flex-1 text-sm text-gray-600">股权类型：<span className="text-[#c4161c]">{info.equity_type}</span></span>
              </div>
              <div className="flex justify-between">
                <span className="flex-1 text-sm text-gray-600">交易机构：<span className="text-[#c4161c]">{info.exchange}</span></span>
                <span className="flex-1 text-sm text-gray-600">股权数量：<span className="text-[#c4161c]">{info.total_shares}</span></span>
              </div>
              <div>
                <span className="text-sm text-gray-600">股权：<span className="text-[#c4161c]">{info.per_person_limit}</span></span>
              </div>
              <div>
                <span className="text-sm text-gray-600">当前股权单价：<span className="text-[#c4161c]">{info.unit_price}元《{info.unit_price_note}》</span></span>
              </div>
              <div>
                <span className="text-sm text-gray-600">您当前持有的股权价值：<span className="text-[#c4161c]">{Number(info.user_equity_value).toLocaleString()}元</span></span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center">加载中...</p>
          )}
        </div>
      </div>

      {/* Wallet section */}
      <div className="px-4 mt-4">
        <div
          className="rounded-2xl p-4 shadow-sm"
          style={{
            backgroundImage: 'url(/images/share_qianbao.png)',
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="text-base text-white">我的钱包</h3>
            <button
              onClick={openHistory}
              className="text-xs text-white bg-white/20 px-3 py-1 rounded-full"
            >
              购买记录
            </button>
          </div>
          <div className="flex items-start justify-center gap-5 bg-white rounded-md p-3">
            <div className="flex flex-col items-center w-[150px]">
              <p className="text-xs text-gray-500">股权余额</p>
              <p className="text-xl text-black">{wallet?.equity_balance ?? 0} <span className="text-sm">股</span></p>

              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => navigate('/sell')}
                  className="flex-1 border border-[#e6212a] text-[#e6212a] text-xs py-[2px] rounded-md font-medium w-[50px]"
                >
                  出售
                </button>
                <button
                  onClick={() => navigate('/wallet')}
                  className="flex-1 bg-[#e6212a] text-white text-xs py-[2px] rounded-lg font-med w-[50px]"
                >
                  明细
                </button>
              </div>

            </div>

            <span className="h-[40px] w-[2px] mt-[10px]" style={{ background: 'gray' }} />

            <div className="flex flex-col items-center w-[150px]">
              <p className="text-xs text-gray-500">每日提现额度</p>
              <p className="text-xl text-black">{wallet?.daily_withdrawal_limit?.toLocaleString() ?? 0} <span className="text-sm">元</span></p>

              <div className="flex gap-3 mt-2">
                <button
                  onClick={() => navigate('/wallet')}
                  className="flex-1 bg-[#e6212a] text-white text-xs py-[2px] rounded-lg font-med w-[50px]"
                >
                  明细
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Total Quota section */}
      <div className="px-4 mt-4">
        <div
          className="rounded-2xl p-4 shadow-sm"
          style={{
            backgroundImage: 'url(/images/share_qianbao.png)',
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="text-base text-white">提现额度</h3>
          </div>
          <div className="flex items-center justify-between bg-white rounded-md p-3">
            <div className="flex flex-col items-center flex-1">
              <p className="text-xs text-gray-500">可提现额度</p>
              <p className="text-xl text-black">{(wallet?.quata_total ?? 0).toLocaleString()} <span className="text-sm">元</span></p>
            </div>
            <button
              onClick={() => setShowWithdrawModal(true)}
              className="bg-[#e6212a] text-white text-xs py-[6px] px-5 rounded-lg font-medium"
            >
              提现
            </button>
          </div>
        </div>
      </div>

      {/* Purchase tiers */}
      <div className="px-4 mt-[20px] mb-[20px] pb-6 bg-[#fbf7f0] rounded-2xl border border-[#f0f0f0] mx-5 pt-[30px]" style={{ boxShadow: '0 0 5px #c5c5c5ff' }}>
        {tiers.map(tier => (
          <div key={tier.id} className="rounded-2xl shadow-sm relative z-1 mb-[30px]" style={{ backgroundImage: 'url(/images/share_ditu.png)', backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat' }}>
            {/* Tier header */}
            <div className="relative">
              <img src="/images/share_fahang.png" alt="" width={200} className="absolute top-[-10px]" />
              <img src="/images/share_rexiao.png" alt="热销" width={80} className="absolute top-[-20px] right-[-20px] z-2" />
            </div>

            {/* Tier info */}
            <div className="grid grid-cols-3 gap-2 pb-1 text-center pt-[20px] relative">
              <div>
                <p className="text-xs text-gray-500 mb-1">赠送股权</p>
                <p className="text-lg text-[#c4161c]">{tier.shares_amount}<span className="text-xs font-normal">股</span></p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">每日提现额度</p>
                <p className="text-lg text-[#c4161c]">{tier.daily_withdrawal_limit}<span className="text-xs font-normal">元</span></p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">额度售价</p>
                <p className="text-lg text-[#c4161c]">{tier.price}<span className="text-xs font-normal">元</span></p>
              </div>
            </div>

            {/* Purchase button */}
            <div className="px-4 pb-4">
              <button
                onClick={() => setConfirmTier(tier)}
                disabled={purchasing === tier.id}
                className="w-full absolute bottom-[-10px]"
              >
                <img src="/images/share_liji.png" alt="立即抢购" className="w-[200px] mx-auto" />
                <span className="text-white text-sm absolute left-1/2 top-[3px] -translate-x-[50%]">立即购买</span>
              </button>
            </div>
          </div>
        ))}

        {tiers.length === 0 && (
          <div className="text-center text-sm text-gray-400 py-8">加载中...</div>
        )}
      </div>

      {/* Loading overlay */}
      {purchasing !== null && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl px-8 py-6 flex flex-col items-center shadow-xl">
            <svg className="animate-spin w-10 h-10 text-[#c4161c] mb-3" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-sm text-gray-700">正在创建订单...</p>
          </div>
        </div>
      )}

      {/* Confirmation modal */}
      {confirmTier && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setConfirmTier(null)}>
          <div className="bg-white rounded-2xl w-[320px] overflow-hidden shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="bg-[#c4161c] px-5 py-4">
              <h3 className="text-white font-bold text-base text-center">确认购买</h3>
            </div>
            <div className="px-5 py-5 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">赠送股权</span>
                <span className="text-sm font-bold text-gray-800">{confirmTier.shares_amount} 股</span>
              </div>
              <div className="border-t border-dashed border-gray-200" />
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">每日提现额度</span>
                <span className="text-sm font-bold text-gray-800">{confirmTier.daily_withdrawal_limit} 元</span>
              </div>
              <div className="border-t border-dashed border-gray-200" />
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">额度售价</span>
                <span className="text-base font-bold text-[#c4161c]">¥{confirmTier.price}</span>
              </div>
            </div>
            <div className="flex gap-3 px-5 pb-5">
              <button
                onClick={() => setConfirmTier(null)}
                className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm text-gray-600"
              >
                取消
              </button>
              <button
                onClick={() => purchase(confirmTier.id)}
                disabled={purchasing === confirmTier.id}
                className="flex-1 py-2.5 rounded-xl bg-[#c4161c] text-white font-medium text-sm disabled:opacity-60"
              >
                {purchasing === confirmTier.id ? '购买中...' : '确认购买'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purchase history modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowHistoryModal(false)}>
          <div className="bg-white rounded-2xl w-[340px] max-h-[70vh] overflow-hidden shadow-xl flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="bg-[#c4161c] px-5 py-4 shrink-0">
              <h3 className="text-white font-bold text-base text-center">购买记录</h3>
            </div>
            <div className="flex-1 overflow-y-auto">
              {history.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {history.map(record => (
                    <div key={record.id} className="px-4 py-3">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-800">
                          {record.shares_amount}股 / 额度{record.daily_withdrawal_limit}元
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${record.status === 'approved' ? 'bg-green-50 text-green-600' :
                          record.status === 'pending' ? 'bg-orange-50 text-orange-600' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                          {record.status === 'approved' ? '已完成' :
                            record.status === 'pending' ? '待支付' : '已失败'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-400">{record.created_at}</span>
                        <span className="text-sm text-[#c4161c] font-medium">¥{record.price}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-sm text-gray-400 py-10">暂无购买记录</div>
              )}
            </div>
            <div className="px-5 py-4 shrink-0">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="w-full py-2.5 rounded-xl bg-[#c4161c] text-white font-medium text-sm"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Withdraw confirmation modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowWithdrawModal(false)}>
          <div className="bg-white rounded-2xl w-[320px] overflow-hidden shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="bg-[#c4161c] px-5 py-4">
              <h3 className="text-white font-bold text-base text-center">确认提现</h3>
            </div>
            <div className="px-5 py-5 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">提现金额</span>
                <span className="text-base font-bold text-[#c4161c]">¥{(wallet?.quata_total ?? 0).toLocaleString()}</span>
              </div>
              <div className="border-t border-dashed border-gray-200" />
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">转入账户</span>
                <span className="text-sm font-bold text-gray-800">钱包余额</span>
              </div>
            </div>
            <div className="flex gap-3 px-5 pb-5">
              <button
                onClick={() => setShowWithdrawModal(false)}
                className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm text-gray-600"
              >
                取消
              </button>
              <button
                onClick={async () => {
                  setWithdrawing(true);
                  try {
                    await api.post('/quata-total/withdraw');
                    toast.success('提现成功');
                    setShowWithdrawModal(false);
                    api.get('/share/wallet').then(({ data }) => setWallet(data?.data ?? data)).catch(() => { });
                  } catch (err: any) {
                    toast.error(err?.response?.data?.message ?? '提现失败');
                  } finally {
                    setWithdrawing(false);
                  }
                }}
                disabled={withdrawing || (wallet?.quata_total ?? 0) <= 0}
                className="flex-1 py-2.5 rounded-xl bg-[#c4161c] text-white font-medium text-sm disabled:opacity-60"
              >
                {withdrawing ? '处理中...' : '确认提现'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment pending modal */}
      {showPendingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowPendingModal(false)}>
          <div className="bg-white rounded-2xl w-[320px] overflow-hidden shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="bg-[#c4161c] px-5 py-4">
              <h3 className="text-white font-bold text-base text-center">支付确认中</h3>
            </div>
            <div className="px-5 py-6 flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center mb-4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v4" />
                  <path d="M12 16h.01" />
                </svg>
              </div>
              <p className="text-base font-semibold text-gray-800 mb-2">等待支付确认</p>
              <p className="text-sm text-gray-500 text-center leading-relaxed">
                您的支付正在确认中，请耐心等待。确认完成后，股权将自动到账。
              </p>
            </div>
            <div className="px-5 pb-5">
              <button
                onClick={() => setShowPendingModal(false)}
                className="w-full py-2.5 rounded-xl bg-[#c4161c] text-white font-medium text-sm"
              >
                我知道了
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
